import { Chip, LinearProgress, Stack, Typography } from '@mui/material'
import {
    FeatureFlagIdJml,
    FeatureFlagIdProvisioningDrawer,
} from 'c1gen/features'
import {
    RequestcatalogV1RequestCatalogManagementServiceAddAppEntitlements,
    RequestcatalogV1RequestCatalogManagementServiceForceRunBundleAutomation,
    RequestcatalogV1RequestCatalogManagementServiceRemoveAppEntitlements,
    RequestcatalogV1RequestCatalogManagementServiceUpdate,
} from 'c1gen/permissions'
import { C1Button } from 'c1ui'
import { C1MoreButton, C1MoreButtonOptionProps } from 'components/core/button'
import { SimpleConfirmationDialog } from 'components/core/confirmation/dialog/SimpleConfirmationDialog'
import { EmptyList } from 'components/core/empty-list'
import { C1Icon } from 'components/core/icons/C1Icon'
import List from 'components/core/list'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxHook } from 'components/core/list/C1Table/C1TableReduxHook'
import { PluralFacetFilter } from 'components/core/search-filter/facet'
import { QueryFilter } from 'components/core/search-filter/query'
import { HasCapability, HasObjectEditPermission, ReadEditObjectPermissions, useFeatureChecker } from 'components/no-ui/capability'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { EntitlementsDrawer } from 'components/pages/entitlement/drawer'
import { appEntitlementId } from 'components/pages/entitlement/helpers'
import { ResourceTypeDisplayName } from 'components/pages/entitlement/resource-type'
import { AddEntitlements } from 'components/pages/requestcatalog/details/setup/add_entitlements'
import { ManageProvisioningDrawer } from 'components/pages/requestcatalog/details/setup/ManageProvisioningDrawer'
import { useAlert } from 'hooks/useAlert'
import { useDrawer } from 'hooks/useDrawer'
import { AppEntitlementValue, CatalogEntryValue } from 'lib/actions/apps/app-entitlement'
import { editAppEntitlements } from 'lib/actions/request_catalog'
import { C1AppId, CredentialCodeRequesterEntitlementID, SystemAnyAccessEntitlementID } from 'lib/constants/system'
import { useSimpleModal } from 'lib/modals'
import { initialRequestCatalogState, requestCatalogReducer } from 'lib/reducers/request_catalog'
import { Status } from 'lib/rpc'
import { useClientRPC } from 'lib/rpcts'
import { useInitialFilters, useSearch } from 'lib/search'
import { KeyRound } from 'lucide-react'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { AppResourceType } from 'pbts/c1/api/app/v1/app_resource'
import { ActorObjectPermissions } from 'pbts/c1/api/iam/v1/authorization'
import {
    AppEntitlementsInCatalogTermsRequest,
    AppEntitlementsInCatalogTermsRequest_TermType,
    SearchAppEntitlementsInCatalogRequest,
} from 'pbts/c1/api/requestcatalog/v1/requestcatalog'
import { ForceRunBundleAutomationRequest } from 'pbts/c1/api/requestcatalog/v1/requestcatalogmanagement'
import { RequestCatalogManagementServiceClient } from 'pbts/c1/api/requestcatalog/v1/requestcatalogmanagement.client'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
    entitlementsInCatalogTermsApi,
    forceRunBundleAutomationApi,
    searchEntitlementsInCatalogApi,
} from 'redux_toolkit/request-catalogs'

const pageSize = 25

export const ExcludedImmutableEntitlementRefsForAccessProfile = [
    AppEntitlementRef.create({ appId: C1AppId, id: SystemAnyAccessEntitlementID }),
    AppEntitlementRef.create({ appId: C1AppId, id: CredentialCodeRequesterEntitlementID }),
]

type CatalogEntitlementListProps = {
    requestCatalogId: string
    allowManagement?: boolean
    memberCount?: number
    hideEnrolledStatus?: boolean
}

export const CatalogEntitlementList = ({ requestCatalogId, allowManagement = false,
    memberCount, hideEnrolledStatus = false }: CatalogEntitlementListProps): React.ReactElement => {
    const intl = useIntl()
    const toast = useAlert()
    const [showDrawer, closeDrawer, setCanClose] = useDrawer()
    const [objectPermissions] = useState<ActorObjectPermissions>(ReadEditObjectPermissions())
    const requestCatalogSvc = useClientRPC<RequestCatalogManagementServiceClient>(RequestCatalogManagementServiceClient)
    const [requestCatalogState, dispatch] = useReducer(requestCatalogReducer, initialRequestCatalogState)

    useEffect((): void => {
        switch (requestCatalogState.appEntitlements.status) {
            case Status.OK:
                toast(
                    intl.formatMessage({
                        id: 'components_request_catalog_entitlements_updated',
                        defaultMessage: 'Catalog entries updated.',
                    }),
                    'success',
                )
                break
            case Status.ERROR:
                toast(requestCatalogState.appEntitlements.error.message, 'error')
            // no default
        }
    }, [toast, intl, requestCatalogState.appEntitlements])

    const entitlementsEditSubmit = useCallback(
        (entitlements: Array<AppEntitlementRef>, removed: Array<AppEntitlementRef>, createRequests: boolean): void => {
            editAppEntitlements(dispatch, requestCatalogSvc, requestCatalogId, entitlements, removed, createRequests)
        },
        [requestCatalogId, requestCatalogSvc],
    )

    const addInitialEntitlementsButton = <HasObjectEditPermission permissions={objectPermissions}>
        <HasCapability permission={RequestcatalogV1RequestCatalogManagementServiceAddAppEntitlements}>
            <AddEntitlements
                onSubmit={entitlementsEditSubmit}
                buttonVariant="contained"
                requestCatalogId={requestCatalogId}
                memberCount={memberCount}
                buttonText={intl.formatMessage({
                    id: 'components_request_catalog_add_entitlements_text',
                    defaultMessage: 'Manage entitlements',
                })}
            />
        </HasCapability>
    </HasObjectEditPermission>

    const entitlementColumns: Array<C1TableReduxColumn<CatalogEntryValue>> = useMemo(
        () => [
            {
                field: 'app',
                label: <FormattedMessage
                    id="components_request_catalog_list_heading_app"
                    defaultMessage="App"
                />,
                Component: ({ row }): React.ReactElement => (<AppDisplayName app={row.app || App.create()} />),
            },
            {
                field: 'resource_type',
                label: <FormattedMessage
                    id="components_request_catalog_list_heading_resource_type"
                    defaultMessage="Resource type"
                />,
                Component: ({ row }): React.ReactElement => (<ResourceTypeDisplayName
                    resourceType={row.appResourceType || AppResourceType.create()}
                />

                ),
            },
            {
                field: 'entitlement',
                label: <FormattedMessage
                    id="components_request_catalog_entitlement_list_heading"
                    defaultMessage="Entitlement"
                />,
                Component: ({ row }): React.ReactElement => {
                    const entitlementDrawerContent = <EntitlementsDrawer entitlementId={row.id} appId={row.appId} />
                    const entitlementAppIsDeleted = row.deletedAt || row.app?.deletedAt
                    return (
                        <Stack direction="row" spacing={1} alignItems="Center" sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                            <C1EntitlementDisplayName excludeDetails entitlement={row} drawerContent={entitlementDrawerContent} />
                            {entitlementAppIsDeleted && (<Chip color="error" label="deleted" />)}
                        </Stack>
                    )
                },
            },
            {
                field: 'enrolled_status',
                label: <FormattedMessage
                    id="components_request_catalog_entitlement_list_enrolled_status_heading"
                    defaultMessage="Provisioning status"
                />,
                shouldRender: (hasFeature): boolean => (hasFeature(FeatureFlagIdJml) && !hideEnrolledStatus) || false,
                minWidth: '300px',
                Component: ({ row }): React.ReactElement => {
                    if (memberCount && row.enrolledMembersCount !== undefined) {
                        return <>
                            <LinearProgress
                                sx={{ maxWidth: '120px', width: '100%', mr: 2 }}
                                color={row.enrolledMembersCount < memberCount ? 'primary' : 'success'}
                                value={(row.enrolledMembersCount / memberCount) * 100}
                                variant="determinate"
                            />
                            <Typography>
                                <FormattedMessage
                                    defaultMessage="{enrolled, number} of {total, number} {total, plural, one {user} other {users}}"
                                    id="components_request_catalog_entitlement_list_enrolled_status"
                                    values={{
                                        enrolled: row.enrolledMembersCount,
                                        total: memberCount,
                                    }}
                                />
                            </Typography>
                        </>
                    }
                    return <></>
                },
            },
            {
                field: 'options',
                isSticky: () => true,
                shouldRender: (): boolean => allowManagement,
                flex: 1,
                Component: ({ row }): React.ReactElement => {
                    const hasJml = useFeatureChecker(FeatureFlagIdJml)
                    const hasProvisioningDrawer = useFeatureChecker(FeatureFlagIdProvisioningDrawer)

                    const { mutate: runBundleAutomation, inProgress: forceRunInProgress } = forceRunBundleAutomationApi()

                    const { open } = useSimpleModal()

                    const options: C1MoreButtonOptionProps[] = [
                        {
                            label: intl.formatMessage({
                                defaultMessage: 'Remove',
                                id: 'components_request_catalog_entitlement_list_remove',
                            }),
                            onClick: (): void => { onRemoveEntitlement(row) },
                            permission: RequestcatalogV1RequestCatalogManagementServiceRemoveAppEntitlements,
                        },
                    ]

                    if (hasJml) {
                        if (hasProvisioningDrawer) {
                            options.push({
                                label: intl.formatMessage({
                                    defaultMessage: 'Manage provisioning',
                                    id: 'components_request_catalog_entitlement_list_manage_provisioning',
                                }),
                                onClick: (): void => { onManageProvisioningClick(row) },
                                permission: RequestcatalogV1RequestCatalogManagementServiceForceRunBundleAutomation,
                            })
                        } else {
                            options.push({
                                label: intl.formatMessage({
                                    defaultMessage: 'Provision',
                                    id: 'components_request_catalog_entitlement_list_provision',
                                }),
                                disabled: forceRunInProgress,
                                onClick: (): void => { onProvisionClick(row) },
                                permission: RequestcatalogV1RequestCatalogManagementServiceUpdate,
                            })
                        }
                    }

                    const onRemoveEntitlement = useCallback((entitlement: CatalogEntryValue): void => {
                        editAppEntitlements(
                            dispatch,
                            requestCatalogSvc,
                            requestCatalogId,
                            [],
                            [AppEntitlementRef.create({ id: entitlement?.id, appId: entitlement?.appId })],
                        )
                    }, [])

                    const onProvisionEntitlement = useCallback(async (entitlement: CatalogEntryValue): Promise<void> => {
                        await runBundleAutomation(ForceRunBundleAutomationRequest.create({
                            requestCatalogId,
                            refs: [AppEntitlementRef.create({ id: entitlement.id, appId: entitlement.appId })],
                        }))

                        toast(
                            intl.formatMessage({
                                id: 'success_request_catalog_run_automation_provision',
                                defaultMessage: 'Provision queued for execution',
                            }),
                            'success',
                        )
                    }, [runBundleAutomation])

                    const onProvisionClick = useCallback(async (entitlement: CatalogEntryValue): Promise<boolean> => {
                        return open(SimpleConfirmationDialog, {
                            onConfirm: async () => onProvisionEntitlement(entitlement),
                            title: intl.formatMessage({
                                defaultMessage: 'Provision entitlement to enrolled users',
                                id: 'components_request_catalog_entitlement_list_provision_title',
                            }),
                            details: <>
                                <Typography mb={2}>
                                    <FormattedMessage id="components_request_catalog_entitlement_list_provision_details" defaultMessage="This action will create requests for enrolled users who are not currently granted this entitlement:" />
                                </Typography>
                                <C1EntitlementDisplayName entitlement={entitlement} />
                            </>,
                            inProgress: forceRunInProgress,
                        })()
                    }, [open, forceRunInProgress, onProvisionEntitlement])

                    const onManageProvisioningClick = useCallback(async (entitlement: AppEntitlementValue): Promise<void> => {
                        showDrawer(<ManageProvisioningDrawer
                            title={intl.formatMessage({
                                id: 'components_request_catalog_entitlement_list_manage_provisioning',
                                defaultMessage: 'Manage entitlement provisioning',
                            })}
                            closeDrawer={closeDrawer}
                            setCanClose={setCanClose}
                            catalogId={requestCatalogId}
                            entitlement={entitlement}
                        />)
                    }, [])

                    return <C1MoreButton
                        options={options}
                    />
                },
            },
        ],
        [
            allowManagement,
            intl,
            memberCount,
            requestCatalogId,
            requestCatalogSvc,
            showDrawer,
            closeDrawer,
            setCanClose,
            toast,
            hideEnrolledStatus,
        ],
    )

    const initialFilters = useInitialFilters(SearchAppEntitlementsInCatalogRequest)

    const emptyReq = useMemo<Partial<SearchAppEntitlementsInCatalogRequest>>(
        () => ({
            catalogId: requestCatalogId,
            expandMask: { paths: ['*'] },
        }),
        [requestCatalogId],
    )

    const [filter, setFilter] = useState<SearchAppEntitlementsInCatalogRequest>(
        SearchAppEntitlementsInCatalogRequest.create({
            ...initialFilters,
            ...emptyReq,
        }),
    )

    const { canClear, clear, onChange, onQueryChange } = useSearch(
        SearchAppEntitlementsInCatalogRequest,
        filter,
        setFilter,
        emptyReq,
    )

    const search = useMemo(() => <Stack direction="row" spacing={2}>
        <QueryFilter
            autoFocus
            query={filter?.query || ''}
            setQuery={(q): void => onQueryChange(q as unknown as string, true)}
            onChange={onQueryChange}
        />
        <PluralFacetFilter<AppEntitlementsInCatalogTermsRequest, 'appIds'>
            onChange={onChange}
            filter={filter}
            term={AppEntitlementsInCatalogTermsRequest_TermType.APPLICATION_IDS}
            filterKey="appIds"
            label={intl.formatMessage({
                id: 'catalog_entitlement_filter_apps',
                defaultMessage: 'App',
            })}
            hook={entitlementsInCatalogTermsApi.useLazyGetSearchTerms}
            widePopper
        />
        {canClear && (
            <C1Button onClick={clear} variant="text" color="info">
                <FormattedMessage id="components_catalog_entitlement_search_index_clear" defaultMessage="Clear" />
            </C1Button>
        )}
    </Stack>, [filter, canClear, clear, onQueryChange, onChange, intl])

    const table = useMemo(() => <C1TableReduxHook<SearchAppEntitlementsInCatalogRequest, CatalogEntryValue>
        cols={entitlementColumns}
        hook={searchEntitlementsInCatalogApi.useSearchAppEntitlementsInCatalog}
        invalidateHook={searchEntitlementsInCatalogApi.useInvalidateSearchAppEntitlementsInCatalog}
        request={filter}
        hookFlags={{
            pageSize,
            notify: true,
            autoReload: true,
            disableEssentialTableExistenceCheck: true,
        }}
        getId={appEntitlementId}
        emptyState={<EmptyList
            text={<FormattedMessage id="components_request_catalog_empty_no_entitlements_found" defaultMessage="No entries found." />}
            icon={<C1Icon icon={KeyRound} />}
        />}
    />, [entitlementColumns, filter])

    return <>
        <Stack direction="row" spacing={2} width="100%" justifyContent="space-between">
            {search}
            {allowManagement && addInitialEntitlementsButton}
        </Stack>
        <List<AppEntitlementValue>
            table={table}
            titleVariant="h5"
        />
    </>
}
